import { FC, useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import {
  Input,
  MLPVersion,
  Button,
  ButtonSize,
  ButtonTypes,
  ButtonStyles,
  Theme,
  Modal,
  Checkbox,
  DropDown,
  DatePicker,
  InputTypes,
  FAIcon,
  IconStyle,
  InputValidations,
  useToast,
  Notification,
  Tooltip,
} from '../';
import type { DropDownDataInterface } from '../';
import { formatDateDMYYYY } from '../../utils';
import { DynamicObjectInterface } from '../../../sdk/type';
import { AttributeSearchProps, SearchDataStructure, SearchValue } from './types/propTypes';
import { AttributeTypes } from './types/enum';
import styles from './AttributeSearch.module.scss';

const MAX_FILTERED_DATA = 10;

interface DropDownResponse {
  id: string;
  title: string;
}

const AttributeSelectionModal = ({
  labels,
  data,
  onModalClose,
  onConfirm,
  onCancel,
}: {
  labels: {
    confirm: string;
    cancel: string;
    maxValue: string;
    modalTitle: string;
  };
  data: SearchDataStructure[];
  onModalClose: () => void;
  onConfirm: (selectedItems: SearchDataStructure[]) => void;
  onCancel: () => void;
}) => {
  const [displayData, setDisplayData] = useState(cloneDeep(data)); //clone deep to to break reference of modal items vs actual displayed items

  const handleCheckboxChange = (index: number) => {
    const updatedDisplayData = [...displayData];
    const selectedAttributes = updatedDisplayData.filter((attribute) => attribute.visibility);
    const allUnchecked = updatedDisplayData.every((checkbox) => !checkbox.visibility);

    if (selectedAttributes.length === 1 && updatedDisplayData[index].visibility) {
      // At least one checkbox should remain checked.
      return;
    }

    if (selectedAttributes.length >= MAX_FILTERED_DATA && !updatedDisplayData[index].visibility) {
      return;
    }

    // If the attribute is mandatory, don't allow unchecking it
    if (updatedDisplayData[index].mandatory) {
      return;
    }

    if (allUnchecked && !updatedDisplayData[index].visibility) {
      updatedDisplayData[index].visibility = true;
    }

    updatedDisplayData[index].visibility = !updatedDisplayData[index].visibility;
    setDisplayData(updatedDisplayData);
  };

  const { modalTitle, confirm, cancel, maxValue } = labels;

  return (
    <Modal
      headline={modalTitle}
      primaryButtonLabel={confirm}
      primaryButton={
        <Button
          title={confirm}
          type={ButtonTypes.BUTTON}
          onClick={() => onConfirm(displayData)}
          buttonStyle={ButtonStyles.SOLID}
          size={ButtonSize.SMALL}
        />
      }
      secondaryButton={
        <Button
          title={cancel}
          type={ButtonTypes.BUTTON}
          onClick={() => onCancel()}
          buttonStyle={ButtonStyles.OUTLINE}
          size={ButtonSize.SMALL}
        />
      }
      closeIcon={false}
      onClose={onModalClose}
      containerWidth="940px"
    >
      <>
        <label>
          {displayData.filter((checkbox) => checkbox.visibility).length}
          {maxValue}
        </label>
        <div className={styles['checkboxes']}>
          {displayData.map((checkbox, index) => (
            <Checkbox
              key={checkbox.key}
              className={styles['checkbox']}
              disabled={checkbox.mandatory}
              text={checkbox.name}
              checked={checkbox.visibility}
              theme={Theme.BLACK}
              onChange={() => handleCheckboxChange(index)}
            />
          ))}
        </div>
      </>
    </Modal>
  );
};

const getAddAttributeLabel = (
  numOfMandatoryTrueAttributes: number,
  length: number,
  labelAdd: string,
  labelRemove: string,
) => (numOfMandatoryTrueAttributes === length ? labelAdd : labelRemove);

//2DO to remove once the reset is solved on framework side
// const resetButtonTheme = (isResetDisabled: boolean) => (isResetDisabled ? Theme.BLACK : Theme.PURPLE);

const hasMultipleAttributes = (numOfMandatoryTrueAttributes: number, selectedAttributesDataLength: number) =>
  numOfMandatoryTrueAttributes === 0 && selectedAttributesDataLength > 1;

const initConfigData = (attribute: DynamicObjectInterface) => ({
  ...attribute,
  inputStatus: InputValidations.DEFAULT,
  visibility: attribute.mandatory ?? attribute.visibility,
});

const ENTER_KEY = 'Enter';

export const AttributeSearch: FC<AttributeSearchProps> = (props: AttributeSearchProps) => {
  const { t } = useTranslation();
  const toggleShowMoreLabel = t('admin.attributeSearch.toggleShowMore');
  const labels = {
    maxValue: `${t('admin.attributeSearch.maxValue')}`,
    // specialCharacterWarning: `${t('admin.attributeSearch.specialCharacterWarning')}`,
    addAttribute: `${t('admin.attributeSearch.addAttribute')}`,
    moreAttribute: `${t('admin.attributeSearch.moreAttribute')}`,
    lessAttribute: `${t('admin.attributeSearch.lessAttribute')}`,
    searchBy: `${t('admin.attributeSearch.searchBy')} `,
    addRemoveAttribute: `${t('admin.attributeSearch.addRemoveAttribute')}`,
    modalTitle: `${t('admin.attributeSearch.attributeSelectionModalTitle')}`,
    reset: `${t('admin.attributeSearch.reset')}`,
    search: `${t('admin.attributeSearch.search')}`,
    confirm: `${t('admin.attributeSearch.confirm')}`,
    cancel: `${t('admin.attributeSearch.cancel')}`,
    msg: `${t('admin.attributeSearch.warning')}`,
  };

  const { data, onSearch, onReset, response: initialResponse, searchByStrip, hideAddButton, defaultSearchOptions } = props;
  const [response, setResponse] = useState(initialResponse ?? {}); //to keep/update whatever changes on attribute inputs for output response object for POD
  const [searchByValues, setSearchByValues] = useState(initialResponse ?? {}); //to display search by strip values using different format
  const [modalStatus, setModalStatus] = useState(false);
  const [configData, setConfigData] = useState<SearchDataStructure[]>(data.map(initConfigData));
  const [isResetDisabled, setIsResetDisabled] = useState(true);
  const [displayDate, setDisplayDate] = useState(true);
  const [showSearchByStrip, setShowSearchByStrip] = useState(false);
  const { addToast, deleteToast } = useToast();
  const [numOfMandatoryTrueAttributes, setNumOfMandatoryTrueAttributes] = useState(0);
  const [numOfVisibleAttributes, setNumOfVisibleAttributes] = useState(0);

  const onModalOpenClose = () => {
    setModalStatus(!modalStatus);
  };

  const [selectedAttributesData, setSelectedAttributesData] = useState<SearchDataStructure[]>([]);

  const [showLessAttribute, setShowLessAttribute] = useState(false);

  const handleInputChange = (key: string, value: string | number | Date | object) => {
    if (value instanceof Date) {
      //for Date attribute
      if (key.endsWith('_to')) {
        //date range to
        const mainKey = key.replace('_to', '');
        setResponse((prevResponse) => ({
          ...prevResponse,
          [mainKey]: [prevResponse[mainKey], value], //append to-date into date range array
        }));
      } else {
        //date object
        setResponse((prevResponse) => ({
          ...prevResponse,
          [key]: value,
        }));
      }
    } else {
      setResponse((prevResponse) => ({
        ...prevResponse,
        [key]: value,
      }));
    }
  };

  const filterOptionLocalStorage = sessionStorage.getItem('FilterOptions')
  useEffect(() => {
    if (defaultSearchOptions && filterOptionLocalStorage) {
      Object.entries(defaultSearchOptions).forEach(([key, value]) => {
        handleInputChange(key, value as string | number | Date | object);
        setShowLessAttribute(true);
        setIsResetDisabled(false);
        setShowSearchByStrip(true);
        let updatedSearchValues = { ...defaultSearchOptions }; // Create a copy of defaultSearchOptions

        Object.entries(defaultSearchOptions).forEach(([key, value]) => {
          if (value instanceof Date) {
            updatedSearchValues[key] = formatDateDMYYYY(value);
          } else if (typeof value === "object" && value !== null) {
            const nestedObject = value as { title: string };
            updatedSearchValues[key] = nestedObject.title;
          } else {
            updatedSearchValues[key] = value;
          }
        });

        setSearchByValues(updatedSearchValues);
      });
    } else if (!defaultSearchOptions && !filterOptionLocalStorage) {
      //if no localstorange and no default search, should reset the filter fields.
      setShowLessAttribute(false);
      setIsResetDisabled(true);
      setShowSearchByStrip(false);
    }
  }, [defaultSearchOptions, filterOptionLocalStorage]);

  const handleReset = () => {
    const emptyResponse: SearchValue = {};
    const emptySearchByValues: SearchValue = {};

    // Reset the response and searchByValues states to empty objects
    setResponse(emptyResponse);
    setSearchByValues(emptySearchByValues);

    // Reset the configData state to update the inputStatus of each attribute
    const updatedConfigData = configData.map((attribute) => ({
      ...attribute,
      inputStatus: InputValidations.DEFAULT,
    }));
    setConfigData(updatedConfigData);
    onReset(emptyResponse);
    // Update other state variables as needed
    setIsResetDisabled(true);
    setShowSearchByStrip(false);
    setShowLessAttribute(false);
    setDisplayDate(false);
  };

  const attributeSearchWarningHandler = () => {
    const id = 'attribute-search';
    addToast({
      id,
      timeout: 3000,
      content: (
        <Notification
          hasNotificationIcon={true}
          theme={Theme.WARNING}
          onCloseClick={() => deleteToast(id)}
          message={labels.msg}
        />
      ),
    });
  };

  const getResponseAttr = (resp: SearchValue, key: string, arrIndex: number = 0) => {
    const value = resp[key];

    if (value !== undefined && value !== null && (typeof value !== 'object' || value instanceof Date)) {
      return value.toString();
    }
    if (Array.isArray(value) && value[arrIndex] !== undefined) {
      return value[arrIndex].toString();
    }

    return '';
  };

  const handleSearch = () => {
    const updatedConfigData = configData.map((attribute) => {


      return attribute;
    });
    onSearch(response);
    setConfigData(updatedConfigData);

    const hasValuesInResponse = Object.values(response).some((value) => value !== '');
    const isOriginalResponseEmpty = Object.keys(response).length === 0;

    setShowLessAttribute(hasValuesInResponse && !isOriginalResponseEmpty);

    setIsResetDisabled(isOriginalResponseEmpty); // Enable the reset button if there are values in the response
    if (isOriginalResponseEmpty) {
      attributeSearchWarningHandler();
    }

    const updatedSearchValues = getSearchByValue();
    const hasValuesInSearchBy = Object.values(updatedSearchValues).some((value) => value !== '');
    const hasAttributesWithValues = selectedAttributesData
      .slice(numOfMandatoryTrueAttributes > 0 ? 0 : 1)
      .some(
        (attribute) =>
          attribute.visibility &&
          updatedSearchValues[attribute.key] !== undefined &&
          updatedSearchValues[attribute.key] !== '',
      );

    setShowLessAttribute(hasValuesInResponse && !isOriginalResponseEmpty);
    setIsResetDisabled(isOriginalResponseEmpty);

    // Check both conditions to determine whether to show searchByStrip
    setShowSearchByStrip(hasValuesInSearchBy && hasAttributesWithValues);

    //Set Search by value to display
    setSearchByValues(updatedSearchValues);
  };

  const toggleShowLessAttribute = () => {
    setShowLessAttribute((prevShowLessAttribute) => !prevShowLessAttribute);
  };

  const getSearchByValue = () => {
    const updatedSearchByValues = { ...searchByValues };
    Object.keys(response).forEach((key) => {
      const attr = response[key];
      if (attr instanceof Date) {
        //for Date attribute
        updatedSearchByValues[key] = formatDateDMYYYY(attr);
      } else if (Array.isArray(attr)) {
        //for Date Range
        if (attr.every((item) => item instanceof Date)) {
          const formattedFromDate = formatDateDMYYYY(attr[0]);
          const formattedToDate = formatDateDMYYYY(attr[1]);
          updatedSearchByValues[key] = `${formattedFromDate} to ${formattedToDate}`;
        } else if (
          attr.every(
            (item) =>
              typeof item === 'object' && 'title' in item && typeof (item as DropDownResponse).title === 'string',
          )
        ) {
          //for Dropdown multi-select
          updatedSearchByValues[key] = attr.map((item) => (item as DropDownResponse).title.toString()).join(', ');
        }
      } else if (typeof attr === 'object' && 'title' in attr && typeof (attr as DropDownResponse).title === 'string') {
        //for Dropdown single-select
        updatedSearchByValues[key] = (attr as DropDownResponse).title;
      } else {
        updatedSearchByValues[key] = attr;
      }
    });

    return updatedSearchByValues;
  };

  const popup = (
    <div data-testid="test-pop-up">
      {modalStatus && (
        <AttributeSelectionModal
          labels={labels}
          data={configData}
          onModalClose={() => onModalOpenClose()}
          onConfirm={(displayData) => {
            setConfigData(displayData);
            onModalOpenClose();
          }}
          onCancel={() => {
            onModalOpenClose();
          }}
        />
      )}
    </div>
  );

  const getTextBoxFieldNumber = (attribute: SearchDataStructure) => (
    <div className={styles['input-container']} data-testid="test-input-container-numeric">
      <Input
        data-testid="input-test-id"
        key={attribute.key}
        type={InputTypes.NUMBER}
        name={attribute.name}
        value={getResponseAttr(response, attribute.key)}
        placeholder={attribute.placeholder ?? `${labels.searchBy} ${attribute.name}`}
        mlpVersion={MLPVersion.ONE}
        onChange={(e) => {
          handleInputChange(attribute.key, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === ENTER_KEY) {
            handleSearch();
          }
        }}
      />
    </div>
  );

  const getTextBoxFieldText = (attribute: SearchDataStructure) => (
    <div className={styles['input-container']} data-testid="test-input-container-text">
      <Input
        data-testid="input-test-name"
        key={attribute.key}
        type={InputTypes.TEXT}
        name={attribute.name}
        // message={
        //   InputValidations.INVALID && HasSpecialCharacters(getResponseAttr(response, attribute.key))
        //     ? `${labels.specialCharacterWarning}`
        //     : ''
        // }
        // status={
        //   HasSpecialCharacters(getResponseAttr(response, attribute.key))
        //     ? InputValidations.INVALID
        //     : InputValidations.DEFAULT
        // }
        value={getResponseAttr(response, attribute.key)}
        placeholder={attribute.placeholder ?? `${labels.searchBy}${attribute.name}`}
        mlpVersion={MLPVersion.ONE}
        onChange={(e) => {
          handleInputChange(attribute.key, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === ENTER_KEY) {
            handleSearch();
          }
        }}
      />
    </div>
  );

  const getDropDownSingleSelectField = (attribute: SearchDataStructure) => (
    <div className={styles[`dropdown-single-select`]} data-testid="test-dropdown-container">
      <DropDown
        name="attribute-dropdown"
        className={styles[`dropdown-box`]}
        data-testid={'test-dropdown'}
        mlpVersion={MLPVersion.ONE}
        disabledFilter
        value={response[attribute.key] ? [(response[attribute.key] as DropDownResponse).title?.toString()] : []}
        data={attribute.dataset?.map((data: { key: string; value: string; title?: string }) => ({
          id: data.key,
          title: data.title ?? data.value,
          value: data.value,
        }))}
        configData={{
          label: 'title',
          onSelect: (selectedOption: DropDownDataInterface) => {
            handleInputChange(attribute.key, selectedOption);
          },
        }}
        placeholder={attribute.placeholder ?? `${labels.searchBy}${attribute.name}`}
      />
    </div>
  );
  const getDropDownMultiSelectField = (attribute: SearchDataStructure) => (
    <div className={styles[`dropdown-multiselect`]} data-testid="test-dropdown-multiple-select-container">
      <DropDown
        name="attribute-dropdown-multi-select"
        className={styles[`dropdown-box`]}
        data-testid={'test-dropdown-multiple-select'}
        multiSelection
        allSelectionOption={attribute.allSelectionOption}
        value={response[attribute.key] ? (response[attribute.key] as DropDownResponse[]).map((item) => item.title) : []}
        data={attribute.dataset?.map((data: { key: string; value: string }) => ({
          id: data.key,
          title: data.value,
        }))}
        configData={{
          label: 'title',
          onSelect: (selectedOption: DropDownDataInterface[]) => {
            handleInputChange(
              attribute.key,
              selectedOption.map((item) => item),
            );
          },
        }}
        placeholder={attribute.placeholder ?? `${labels.searchBy}${attribute.name}`}
      />
    </div>
  );

  const getDateTimeSingleField = (attribute: SearchDataStructure) => (
    <div className={styles['date-container']} data-testid="test-datetime-single-container">
      <DatePicker
        //2DO to remove workaround for when the response comes back empty
        showDateContainer
        displayText={
          response[attribute.key] && displayDate
            ? undefined
            : attribute.placeholder ?? `${labels.searchBy}${attribute.name}`
        }
        data-testid={'test-date-picker-single'}
        placeholder={attribute.placeholder ?? `${labels.searchBy}${attribute.name}`}
        calendarHeading={attribute.name}
        value={response[attribute.key] ? new Date(getResponseAttr(response, attribute.key)) : undefined}
        onChange={(date) => {
          handleInputChange(attribute.key, date ?? '');
          setDisplayDate(true);
        }}
      />
    </div>
  );

  const getDateTimeRangeField = (attribute: SearchDataStructure) => (
    <div className={styles['date-container']} data-testid="test-datetime-range">
      <DatePicker
        showDateContainer
        selectRange
        //2DO to remove workaround for when the response comes back empty
        displayText={
          response[attribute.key] && displayDate
            ? undefined
            : attribute.placeholder ?? `${labels.searchBy}${attribute.name}`
        }
        placeholder={attribute.placeholder ?? `${labels.searchBy}${attribute.name}`}
        calendarHeading={attribute.name}
        value={response[attribute.key] ? new Date(getResponseAttr(response, attribute.key, 0)) : undefined}
        valueTo={response[attribute.key] ? new Date(getResponseAttr(response, attribute.key, 1)) : undefined}
        onChange={(dateFrom, dateTo) => {
          setDisplayDate(true);
          handleInputChange(attribute.key, dateFrom ?? '');
          if (dateTo) {
            handleInputChange(`${attribute.key}_to`, dateTo);
          }
        }}
      />
    </div>
  );

  const getAttributeField = (attribute: SearchDataStructure) => {
    switch (attribute.type) {
      case AttributeTypes.TextboxNumber:
        return getTextBoxFieldNumber(attribute);
      case AttributeTypes.Textbox:
        return getTextBoxFieldText(attribute);
      case AttributeTypes.DropdownSingleSelect:
        return getDropDownSingleSelectField(attribute);
      case AttributeTypes.DropdownMultiSelect:
        return getDropDownMultiSelectField(attribute);
      case AttributeTypes.DateTimeSingle:
        return getDateTimeSingleField(attribute);
      case AttributeTypes.DateTimeRange:
        return getDateTimeRangeField(attribute);
      default:
        return null;
    }
  };

  const searchResetButtons = (
    <>
      <Button
        title={labels.search}
        type={ButtonTypes.BUTTON}
        size={ButtonSize.SMALL}
        onClick={handleSearch}
        data-testid="test-search-button"
      />
      <Button
        title={labels.reset}
        type={ButtonTypes.BUTTON}
        size={ButtonSize.SMALL}
        buttonStyle={ButtonStyles.OUTLINE}
        onClick={handleReset}
        data-testid="test-reset-button"
        disabled={isResetDisabled}
      />
    </>
  );

  const addAttributeButton = (
    <button data-testid="test-add-remove" className={styles['link']} onClick={() => onModalOpenClose()}>
      {/* All mandatory > Add Attributes, else > Add/Remove Attributes */}
      {getAddAttributeLabel(
        numOfMandatoryTrueAttributes,
        selectedAttributesData.length,
        labels.addAttribute,
        labels.addRemoveAttribute,
      )}
    </button>
  );

  const getChevronIconName = () => (showLessAttribute ? 'chevron-down' : 'chevron-up');

  useEffect(() => {
    const selectedAttributesData = configData
      .filter((attribute) => attribute.visibility && Object.values(AttributeTypes).includes(attribute.type))
      .slice(0, MAX_FILTERED_DATA);
    setSelectedAttributesData(selectedAttributesData);

    const numOfMandatoryTrueAttributes = configData.filter(
      (attribute) => attribute.mandatory && attribute.visibility,
    ).length;
    setNumOfMandatoryTrueAttributes(numOfMandatoryTrueAttributes);

    let numOfVisibleAttributes: number;

    if (showLessAttribute) {
      // If showLessAttribute is true
      if (numOfMandatoryTrueAttributes > 0) {
        numOfVisibleAttributes = numOfMandatoryTrueAttributes;
      } else {
        numOfVisibleAttributes = 1;
      }
    } else {
      // If showLessAttribute is false
      numOfVisibleAttributes = selectedAttributesData.length;
    }

    setNumOfVisibleAttributes(numOfVisibleAttributes);
  }, [configData, showLessAttribute]);

  useEffect(() => {
    setConfigData(data.map(initConfigData));
  }, [data]);

  return (
    <div className={styles['attribute-search-container']} data-testid="test-attribute-search-container">
      <div className={styles['attribute-container']} data-testid="test-attribute-container-single">
        {/* Loop through the selected attributes */}

        {/* ---------- Display attributes ---------- */}
        {selectedAttributesData.map((attribute, index) => (
          <div
            className={`${styles['container']} ${((numOfMandatoryTrueAttributes === 0 && index !== 0) ||
              (numOfMandatoryTrueAttributes > 0 && !attribute.mandatory)) &&
              showLessAttribute
              ? styles['hidden']
              : ''
              }`}
            data-testid="test-container"
            key={attribute.key}
          >
            {selectedAttributesData.length > 1 &&
              ((!showLessAttribute && numOfMandatoryTrueAttributes <= 1) || numOfMandatoryTrueAttributes > 1) && (
                <div className={styles['input-header-container']} data-testid="input-header-container-test">
                  <div className={styles['input-header']} data-testid="input-header-test">
                    {attribute.name}
                  </div>
                  {attribute.labelInfo && (
                    <Tooltip
                      message={attribute.labelInfo}
                      preference={attribute.labelInfoPreference}
                      className={styles['attribute-search-tooltip']}
                      data-testid="tooltip-test"
                    />
                  )}
                </div>
              )}
            {getAttributeField(attribute)}
          </div>
        ))}

        {/* ---------- Display Buttons ---------- */}
        {/* if ONLY 1 attribute to display */}
        {selectedAttributesData.length === 1 ? (
          <div className={styles['button-container-single']} data-testid="test-button-container-single">
            {searchResetButtons}
            {!hideAddButton && (
              <div className={styles['add-attribute-single']} data-testid="test-add-single">
                <button
                  className={styles['link']}
                  onClick={() => {
                    onModalOpenClose();
                    setShowLessAttribute(false);
                  }}
                  data-testid="test-button-single"
                >
                  {labels.addAttribute}
                </button>
                <div>{popup}</div>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* To show add attribute when not all data mandatory AND show more attribute OR display only all mandatory */}
            {!hideAddButton &&
              numOfMandatoryTrueAttributes !== configData.length &&
              (!showLessAttribute || numOfMandatoryTrueAttributes === selectedAttributesData.length) && (
                <div
                  className={`${styles['add-attribute']} ${numOfVisibleAttributes > 1 && numOfVisibleAttributes % 3 !== 0 && styles['add-attribute-margin']
                    }`}
                  data-testid="test-add-attribute-multiple"
                >
                  {addAttributeButton}
                  <div>{popup}</div>
                </div>
              )}
          </>
        )}
        {/* if more than 1 attributes to display */}
        {selectedAttributesData.length > 1 && (
          <div className={`${styles['show-less-container']}`} data-testid="test-show-less-container">
            <div
              className={`${styles['button-wrapper']} ${(!hideAddButton && numOfVisibleAttributes > 1 && (numOfVisibleAttributes - 1) % 3 === 0) || //4,7,10
                (hideAddButton && numOfVisibleAttributes > 1 && numOfVisibleAttributes % 3 !== 0) //!3!6!9
                ? styles['margin-top-button']
                : styles['margin-top-small']
                }`}
            >
              {searchResetButtons}
            </div>
          </div>
        )}
      </div>

      {/* ---------- Display Search By Strip ---------- */}
      {showSearchByStrip &&
        searchByStrip &&
        selectedAttributesData.length > numOfVisibleAttributes && //show only when there are hidden attributes
        Object.values(searchByValues).some(
          (value) => value !== '' && (Array.isArray(value) ? value.length > 0 : true),
        ) && (
          <>
            {/* Conditionally render the searchByStrip label and data */}
            {selectedAttributesData
              .slice(numOfMandatoryTrueAttributes > 0 ? 0 : 1)
              .some(
                (attribute) =>
                  attribute.visibility &&
                  searchByValues[attribute.key] !== undefined &&
                  searchByValues[attribute.key] !== '',
              ) && (
                <>
                  {selectedAttributesData.length > 1 && (
                    <div className={styles['search-by-divider']} data-testid="test-search-by-divider" />
                  )}

                  <div className={styles['search-by-container']} data-testid="test-search-by-container">
                    <div className={styles['search-by-label']} data-testid="test-search-by-label">
                      {labels.searchBy}
                    </div>
                    {Object.entries(searchByValues).map(([key, value]) => {
                      // const attribute = configData.find((attr) => attr.key === key);
                      // const hasSpecialCharacter =
                      //   attribute?.type === AttributeTypes.Textbox &&
                      //   typeof value === 'string' 
                      return (
                        value !== '' &&
                        // !hasSpecialCharacter && 
                        (
                          <li key={key} className={styles['search-by-value']} data-testid="test-search-by-value">
                            {`${value.toString()}`}
                          </li>
                        )
                      );
                    })}
                  </div>
                </>
              )}
          </>
        )}

      {/* ---------- Display Show More/Less Attributes ---------- */}
      {(hasMultipleAttributes(numOfMandatoryTrueAttributes, selectedAttributesData.length) || //No Mandatory attribute & visible > 1
        (numOfMandatoryTrueAttributes > 0 && selectedAttributesData.length > numOfMandatoryTrueAttributes)) && ( //visible > mandatory attributes
          <>
            <div className={styles['divider']} />
            <div
              className={styles['show-less']}
              onKeyDown={toggleShowLessAttribute}
              role="button"
              aria-labelledby={toggleShowMoreLabel}
              tabIndex={0}
              onClick={toggleShowLessAttribute}
              data-testid="test-show-less"
            >
              <div className={styles['icons']} data-testid="test-icons">
                <FAIcon iconName={getChevronIconName()} theme={Theme.PURPLE} iconStyle={IconStyle.SOLID} />
              </div>
              <div className={styles['more-attribute-text']} data-testid={'show-less-test'}>
                {showLessAttribute ? labels.moreAttribute : labels.lessAttribute}
              </div>
            </div>
          </>
        )}
    </div>
  );
};

export default AttributeSearch;
export { AttributeTypes };
export type { AttributeSearchProps, SearchValue, SearchDataStructure };
